import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheck, FiX } from 'react-icons/fi';
import axios from 'axios';
import { handleUnauthorized } from '../../utils/apiClient';
import { getCustomTemplates } from '../../services/quoteProposalService';
import type { CustomTemplate } from '../../services/quoteProposalService';
import Step1_ClientQuote from './Step1_ClientQuote';
import Step2_SignatureDocsVideo from './Step2_SignatureDocsVideo';
import Step3_PreviewSend from './Step3_PreviewSend';
import { ROUTES } from '../../app/routes';
import { useToast } from '../../context/ToastContext';
import '../../styles/QuoteProposal.css';

/* ═══════════════════════════════════════════════════════
   TOKEN HELPERS
═══════════════════════════════════════════════════════ */
export const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';
export const getAmsToken = () => localStorage.getItem('amsToken') || '';

/* ═══════════════════════════════════════════════════════
   ENV URLS
═══════════════════════════════════════════════════════ */


/* ═══════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════ */
export type UploadedDoc = {
  file: File | null;
  fileName: string;
  fileURL: string;
  fileId: string;
};

export type ExtraDoc = {
  fileURL: string;
  fileName: string;
};

/* ═══════════════════════════════════════════════════════
   UUID GENERATOR
═══════════════════════════════════════════════════════ */
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

/* ═══════════════════════════════════════════════════════
   DEBOUNCE HOOK
═══════════════════════════════════════════════════════ */
function useDebounce(fn: (val: string) => void, delay: number) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  return useCallback(
    (val: string) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => fn(val), delay);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delay]
  );
}

/* ═══════════════════════════════════════════════════════
   STEP DEFINITIONS
═══════════════════════════════════════════════════════ */
const STEPS = [
  { num: 1, label: 'Client & Quote' },
  { num: 2, label: 'Docs & Signature' },
  { num: 3, label: 'Preview & Send' },
];

/* ═══════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════ */
const QuoteStepper: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlTxId = location.pathname.startsWith(ROUTES.QUOTE_PROPOSAL + '/')
    ? location.pathname.slice(ROUTES.QUOTE_PROPOSAL.length + 1) || undefined
    : undefined;
  // showToast(type, message) — same call shape as the old local hook, now backed
  // by the shared app-wide toast (src/context/ToastContext.tsx).
  const { showToast } = useToast();
  const skipLoadDraftRef = useRef(false);
  const isSubmittingRef = useRef(false);

  /* ── Stepper ── */
  const [activeIndex, setActiveIndex] = useState(0);
  const [isProposalGenerated, setIsProposalGenerated] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  /* ── Client ── */
  const [clientName, setClientName] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [search, setSearch] = useState('');
  const [searchClientList, setSearchClientList] = useState<any[]>([]);

  /* ── Document source flags ── */
  const [isDocumentSelected, setIsDocumentSelected] = useState(true);
  const [isDriveSelected, setIsDriveSelected] = useState(false);
  const [isQuoteSelected, setIsQuoteSelected] = useState(false);

  /* ── Uploaded docs (Step 1 direct upload) ── */
  const [documents, setDocuments] = useState<UploadedDoc[]>([
    { file: null, fileName: '', fileURL: '', fileId: '' },
  ]);
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [fileURL, setFileURL] = useState('');
  const [fileNamesForDataTable, setFileNamesForDataTable] = useState<string[]>([]);

  /* ── Drive (Client Profile) selection ── */
  const [selectedDriveData, setSelectedDriveData] = useState({
    folderId: '',
    fileIds: [] as string[],
    fileNames: [] as string[],
    folderIds: [] as string[],
  });

  /* ── AMS Quote ── */
  const [nowCertQuoteData, setNowCertQuoteData] = useState<any[]>([]);
  const [nowCertPolicyData, setNowCertPolicyData] = useState<any[]>([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [selectedQuote, setSelectedQuote] = useState<any>(null);
  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [selectedPolicy, setSelectedPolicy] = useState<any>(null);
  const [coverageDetails, setCoverageDetails] = useState<any[]>([]);
  const [driverList, setDriverList] = useState<any[]>([]);
  const [vehicleList, setVehicleList] = useState<any[]>([]);

  /* ── Additional quotes (multi-quote feature) ── */
  const [selectedQuotesList, setSelectedQuotesList] = useState<any[]>([]);
  const [selectedPoliciesList, setSelectedPoliciesList] = useState<any[]>([]);
  const [quoteCoverageDetails, setQuoteCoverageDetails] = useState<any[][]>([]);
  const [quoteDriverLists, setQuoteDriverLists] = useState<any[][]>([]);
  const [quoteVehicleLists, setQuoteVehicleLists] = useState<any[][]>([]);
  /* Per-slot upload/drive data for additional slots */
  const [additionalSlotFiles, setAdditionalSlotFiles] = useState<{ slotIndex: number; file: File | null; fileName: string }[]>([]);
  const [additionalSlotDrives, setAdditionalSlotDrives] = useState<{ slotIndex: number; driveData: import('./Step1_ClientQuote').DriveData }[]>([]);
  const [restoredAdditionalDocs, setRestoredAdditionalDocs] = useState<{ fileName: string; fileURL: string; fileId: string }[]>([]);

  /* ── Proposal meta ── */
  const [proposalTitle, setProposalTitle] = useState('');
  const [transactionId, setTransactionId] = useState('');

  /* ── Step 2 ── */
  const [teamMemberList, setTeamMemberList] = useState<any>(null);
  const [teamMember, setTeamMember] = useState('');
  const [proposalTeamMembers, setProposalTeamMembers] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('1');
  const [selectedCustomTemplate, setSelectedCustomTemplate] = useState(0);
  const [selectedCustomTemplateName, setSelectedCustomTemplateName] = useState('');
  const [selectedCustomTemplateImages, setSelectedCustomTemplateImages] = useState<string[]>([]);
  const [customTemplateList, setCustomTemplateList] = useState<CustomTemplate[]>([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [extraDocumentList, setExtraDocumentList] = useState<ExtraDoc[]>([]);
  const [dynamicLinks, setDynamicLinks] = useState<{ description: string; url: string }[]>([]);
  const [uploadDocLoading, setUploadDocLoading] = useState(false);
  const [viewDocUrl, setViewDocUrl] = useState('');
  const [viewDocOpen, setViewDocOpen] = useState(false);

  /* ── Company / settings ── */
  const [companyData, setCompanyData] = useState<any>(null);
  const [customBlocks, setCustomBlocks] = useState<any[]>([]);

  /* ── Loading ── */
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [nowcertsPolicyLoading, setNowcertsPolicyLoading] = useState(false);
  const [uploadingDocIndex, setUploadingDocIndex] = useState<number | null>(null);

  /* ── Validation errors ── */
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});

  /* ═══════════════════════════════════════════════════
     401 HANDLER
  ═══════════════════════════════════════════════════ */
  const handle401 = useCallback((err: any) => {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      handleUnauthorized();
    }
  }, []);

  const onViewDocumentClicked = useCallback(async (fileName: string) => {
    if (!transactionId) return;
    try {
      const res = await axios({
        method: 'POST',
        url: `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/viewdocument?transactionId=${transactionId}&fileName=${encodeURIComponent(fileName)}`,
        headers: { Authorization: `Bearer ${getToolboxToken()}`, 'Content-Type': 'application/json' },
      });
      setViewDocUrl(res.data.fileURL || '');
      setViewDocOpen(true);
    } catch (err) {
      handle401(err);
      showToast('error', 'Failed to load document');
    }
  }, [transactionId, handle401, showToast]);

  /* ═══════════════════════════════════════════════════
     A. SEARCH CLIENTS (debounced)
  ═══════════════════════════════════════════════════ */
  const [clientSearchLoading, setClientSearchLoading] = useState(false);

  const searchClients = useCallback(
    async (query: string) => {
      if (!query || query.replaceAll(' ', '').length === 0) {
        setSearchClientList([]);
        setClientSearchLoading(false);
        return;
      }
      setClientSearchLoading(true);
      try {
        const res = await axios.get(`${(process.env.REACT_APP_NOWCERTSAPI || '')}/api/nowcert/customerlist`, {
          params: { searchText: query, nowCertsToken: getAmsToken() },
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
        });
        setSearchClientList(res.data || []);
      } catch (err) {
        handle401(err);
        setSearchClientList([]);
      } finally {
        setClientSearchLoading(false);
      }
    },
    [handle401]
  );

  const debouncedSearch = useDebounce(searchClients, 500);

  /* ── Client selected handler ── */
  const handleClientSelect = useCallback(
    (item: any) => {
      setSearch('');
      setClientName(item.name);
      setClientId(item.databaseId);
      setClientEmail(item.eMail || '');
      setProposalTitle(`Proposal for ${item.name}`);
      setSearchClientList([]);
      setValidationErrors(v => ({ ...v, clientName: false }));
      getNowCertsPoliciesList(item.databaseId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /* ── Search input change ── */
  const handleSearchChange = useCallback(
    (val: string) => {
      setSearch(val);
      setClientName(val);
      if (val.replaceAll(' ', '').length > 0) {
        setClientSearchLoading(true); // show spinner immediately, before debounce fires
        debouncedSearch(val);
      } else {
        setClientSearchLoading(false);
        setSearchClientList([]);
      }
    },
    [debouncedSearch]
  );

  /* ═══════════════════════════════════════════════════
     B. GET NOWCERTS POLICIES LIST
  ═══════════════════════════════════════════════════ */
  const getNowCertsPoliciesList = useCallback(
    async (insuredId: string) => {
      if (!insuredId) return;
      setNowcertsPolicyLoading(true);
      try {
        const res = await axios.get(`${(process.env.REACT_APP_NOWCERTSAPI || '')}/api/nowcert/insuredpolicies`, {
          params: { insuredId, nowCertsToken: getAmsToken() },
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
        });
        const data = res.data || [];
        setNowCertQuoteData(data.filter((p: any) => p.isQuote === true));
        setNowCertPolicyData(data.filter((p: any) => p.isQuote !== true));
      } catch (err) {
        handle401(err);
      } finally {
        setNowcertsPolicyLoading(false);
      }
    },
    [handle401]
  );

  /* ═══════════════════════════════════════════════════
     C. GET POLICY COVERAGE DETAILS
  ═══════════════════════════════════════════════════ */
  const getPolicyCoverageDetailList = useCallback(
    async (policyId: string) => {
      try {
        const res = await axios.get(`${(process.env.REACT_APP_NOWCERTSAPI || '')}/api/nowcert/getpolicyCoverageslist`, {
          params: { policyId, ncToken: getAmsToken() },
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
        });
        const data = res.data || [];
        setCoverageDetails(data);
        return data;
      } catch (err) {
        handle401(err);
        return [];
      }
    },
    [handle401]
  );

  /* ═══════════════════════════════════════════════════
     D. GET DRIVER LIST
  ═══════════════════════════════════════════════════ */
  const getDriverListFn = useCallback(
    async (insuredId: string) => {
      try {
        const res = await axios.get(`${(process.env.REACT_APP_NOWCERTSAPI || '')}/api/nowcert/driverlist`, {
          params: { insuredId, ncToken: getAmsToken() },
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
        });
        setDriverList(res.data || []);
      } catch (err) {
        handle401(err);
      }
    },
    [handle401]
  );

  /* ═══════════════════════════════════════════════════
     E. GET VEHICLE LIST
  ═══════════════════════════════════════════════════ */
  const getVehicleListFn = useCallback(
    async (insuredId: string) => {
      try {
        const res = await axios.get(`${(process.env.REACT_APP_NOWCERTSAPI || '')}/api/nowcert/vehicles`, {
          params: { insuredDatabaseId: insuredId, ncToken: getAmsToken() },
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
        });
        const val = res.data?.value;
        setVehicleList(Array.isArray(val) ? val : []);
      } catch (err) {
        handle401(err);
        setVehicleList([]);
      }
    },
    [handle401]
  );

  /* ═══════════════════════════════════════════════════
     F. GET ALL SETTINGS (company data)
  ═══════════════════════════════════════════════════ */
  const getAllSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/getcompanyhistory?origin=true`, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });
      setCompanyData(res.data || null);
    } catch (err) {
      handle401(err);
    }
  }, [handle401]);

  /* ═══════════════════════════════════════════════════
     H. FETCH CUSTOM BLOCKS FOR TEMPLATE
  ═══════════════════════════════════════════════════ */
  const fetchCustomBlocksForTemplate = useCallback(
    async (templateId: string | number) => {
      try {
        const res = await axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/custom-blocks`, {
          params: { templateId },
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
        });
        const data = res.data?.data ?? [];
        setCustomBlocks(Array.isArray(data) ? data : []);
      } catch (err) {
        handle401(err);
        setCustomBlocks([]);
      }
    },
    [handle401]
  );

  /* ═══════════════════════════════════════════════════
     G. GET TEAM MEMBER LIST
  ═══════════════════════════════════════════════════ */
  const getTeamMemberList = useCallback(async () => {
    try {
      const res = await axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/getteammember`, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });
      const r = res.data?.response ?? res.data;
      setTeamMemberList(r);

      const tplId = r?.templateId ? r.templateId.toString() : '1';
      setSelectedTemplate(tplId);

      if (r?.customTemplateId) {
        setSelectedCustomTemplate(r.customTemplateId);
      } else {
        setSelectedCustomTemplate(0);
        setSelectedCustomTemplateImages([]);
      }
      setSelectedCustomTemplateName(r?.customTemplateName || '');

      fetchCustomBlocksForTemplate(tplId);

      /* Also load the available custom templates for the picker */
      getCustomTemplates().then(list => {
        setCustomTemplateList(list);
        if (r?.customTemplateId) {
          const match = list.find((t: CustomTemplate) => t.id === r.customTemplateId);
          if (match?.images) {
            const raw = match.images;
            const imgs: string[] = Array.isArray(raw)
              ? (typeof raw[0] === 'string'
                  ? (raw[0] as string).split(',').map((s: string) => s.trim()).filter(Boolean)
                  : (raw as string[]))
              : (raw as string).split(',').map((s: string) => s.trim()).filter(Boolean);
            setSelectedCustomTemplateImages(imgs);
          }
        }
      }).catch(() => {});
    } catch (err) {
      handle401(err);
    }
  }, [handle401, fetchCustomBlocksForTemplate]);

  /* ═══════════════════════════════════════════════════
     J. CREATE DRAFT — called on "Next Step" at each step
     Step 0: sends actual files in multipart
     Step 1+: sends fileIds from previous response
  ═══════════════════════════════════════════════════ */
  const createDraft = useCallback(
    async (stepIndex: number) => {
      setLoading(true);
      setProgress(0);
      setProcessing(false);

      const currentTxId = transactionId || generateUUID();
      if (!transactionId) {
        skipLoadDraftRef.current = true;
        setTransactionId(currentTxId);
        navigate(`${ROUTES.QUOTE_PROPOSAL}/${currentTxId}`, { replace: true });
      }

      const data = new FormData();

      if (stepIndex === 0) {
        /* Step 0: only upload files that haven't been uploaded yet (d.fileId is empty).
           If all files are already uploaded (Back then Next), reuse their IDs instead.
           This prevents the same file being uploaded twice and creating duplicate entries. */
        const newFiles = documents.filter(d => d.file && !d.fileId);
        const alreadyUploaded = documents.filter(d => d.fileId);
        if (newFiles.length > 0) {
          newFiles.forEach(d => data.append('files', d.file as File));
          newFiles.forEach(d => { if (d.fileName) data.append('fileNames', d.fileName); });
        } else if (alreadyUploaded.length > 0 || fileIds.length > 0) {
          /* All files already uploaded — behave like step 1+ */
          fileIds.forEach(id => data.append('fileIds', id));
          fileNames.forEach(n => data.append('fileNames', n));
        } else {
          /* Fallback: no fileId on documents yet (first upload attempt) */
          documents.filter(d => d.file).forEach(d => data.append('files', d.file as File));
          documents.filter(d => d.file).forEach(d => { if (d.fileName) data.append('fileNames', d.fileName); });
        }
      } else {
        /* Step 1+: reuse previously returned file IDs */
        fileIds.forEach(id => data.append('fileIds', id));
        fileNames.forEach(n => data.append('fileNames', n));
      }

      /* Additional slot uploaded files (from extra quote slots using Upload tab).
         Only send if the File object is present — it is cleared after the first successful
         upload so that going Back and clicking Next again does not re-upload the same file. */
      if (stepIndex === 0) {
        additionalSlotFiles.forEach(sf => {
          if (sf.file) {
            data.append('files', sf.file, sf.fileName);
            data.append('fileNames', sf.fileName);
          }
        });
      }

      /* Drive files — slot 0 */
      if (isDriveSelected && selectedDriveData.fileIds.length > 0) {
        data.append('amsFileIds', selectedDriveData.fileIds.join(','));
        data.append('folderId', selectedDriveData.folderId);
        data.append('nc_token', getAmsToken());
        data.append('insuredId', clientId);
        selectedDriveData.fileIds.forEach(id => data.append('driveFileIds', id));
        selectedDriveData.folderIds.forEach(fid => data.append('driveFolderIds', fid));
        selectedDriveData.fileNames.forEach(n => data.append('fileNames', n));
      }

      /* Drive files — additional slots (Client Profile tab) */
      if (additionalSlotDrives.length > 0) {
        if (!isDriveSelected) {
          /* Set nc_token / insuredId only if not already set by slot 0 */
          data.append('nc_token', getAmsToken());
          data.append('insuredId', clientId);
        }
        additionalSlotDrives.forEach(sd => {
          sd.driveData.fileIds.forEach(id => data.append('driveFileIds', id));
          sd.driveData.folderIds.forEach(fid => data.append('driveFolderIds', fid));
          sd.driveData.fileNames.forEach(n => data.append('fileNames', n));
        });
      }

      /* Quote / policy details — supports multiple quotes.
         selectedQuotesList[0] is always null (primary quote lives in selectedQuote).
         Always include the primary quote first, then append non-null additional slots. */
      const quoteEntries: { q: any; origIdx: number }[] = [
        ...(selectedQuote ? [{ q: selectedQuote, origIdx: 0 }] : []),
        ...selectedQuotesList
          .map((q, i) => ({ q, origIdx: i }))
          .filter(e => e.origIdx > 0 && e.q != null),
      ];
      if (quoteEntries.length > 0) {
        quoteEntries.forEach(({ q, origIdx }) => {
          const isFirst = origIdx === 0;
          const qObj = {
            TotalPremium: q.totalPremium ?? '',
            PolicyNumber: q.policyNumber ?? '',
            DatabaseId: q.databaseId ?? '',
            CarrierName: q.carrierName ?? '',
            Term: q.policyTermInMonths ?? '',
            EffectiveDate: q.effectiveDate ?? '',
            ExpirationDate: q.expirationDate ?? '',
            DocumentType: Array.isArray(q.linesOfBusiness) && q.linesOfBusiness.length > 0 ? q.linesOfBusiness[0] : '',
            CoverageDetails: isFirst ? (coverageDetails.length > 0 ? coverageDetails : []) : (quoteCoverageDetails[origIdx] || []),
            DriverList: isFirst ? (driverList.length > 0 ? driverList : []) : (quoteDriverLists[origIdx] || []),
            VehicleList: isFirst ? (vehicleList.length > 0 ? vehicleList : []) : (quoteVehicleLists[origIdx] || []),
            QuoteId: q.databaseId ?? '',
            PolicyId: isFirst ? (selectedPolicy?.databaseId || null) : (selectedPoliciesList[origIdx]?.databaseId || null),
          };
          data.append('quoteDetails', JSON.stringify(qObj));
        });
      } else {
        data.append('quoteDetails', '');
      }

      /* Build policyDetails: primary policy first, then every additional slot policy.
         Built independently of quoteEntries so all selected policies are always included. */
      const buildPolicyObj = (p: any) => {
        const policyId = String(p.databaseId ?? p.DatabaseId ?? '').trim();
        if (!policyId) return null;
        return {
          totalPremium: p.totalPremium ?? p.TotalPremium ?? '',
          policyNumber: p.policyNumber ?? p.PolicyNumber ?? '',
          databaseId: policyId,
          carrierName: p.carrierName ?? p.CarrierName ?? '',
          policyTermInMonths: p.policyTermInMonths ?? p.Term ?? '',
          effectiveDate: p.effectiveDate ?? p.EffectiveDate ?? '',
          expirationDate: p.expirationDate ?? p.ExpirationDate ?? '',
          linesOfBusiness: p.linesOfBusiness ?? (p.lineOfBusiness ? [p.lineOfBusiness] : []),
        };
      };
      const policyArray = [
        selectedPolicy ? buildPolicyObj(selectedPolicy) : null,
        ...selectedPoliciesList.slice(1).map((p: any) => p ? buildPolicyObj(p) : null),
      ].filter(Boolean);
      data.append('policyDetails', JSON.stringify(policyArray));
      data.append('documentType', '');

      /* Always-required fields */
      data.append('transactionId', currentTxId);
      data.append('clientId', clientId);
      data.append('clientName', clientName);
      data.append('clientEmail', clientEmail);
      data.append('proposalTitle', proposalTitle);
      data.append('templateId', selectedTemplate);
      data.append('videoUrl', videoUrl || '');
      data.append('fileURL', fileURL);
      data.append('selectedTeamMember', teamMember);
      data.append('selectedProposalMembers', JSON.stringify(proposalTeamMembers));
      data.append('extraDocDetails', JSON.stringify(extraDocumentList));
      data.append('dynamicLinks', JSON.stringify(dynamicLinks || []));
      data.append('customBlockDetails', JSON.stringify(customBlocks || []));

      try {
        const res = await axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/draft`, data, {
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
          onUploadProgress: e => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            setProgress(pct);
            if (pct >= 100) setProcessing(true);
          },
        });

        setTransactionId(res.data.transactionId || currentTxId);

        /* Parse file IDs from response */
        let ids: string[] = [];
        if (Array.isArray(res.data.filesId)) ids = res.data.filesId;
        else if (typeof res.data.filesId === 'string')
          ids = res.data.filesId.split(',').map((s: string) => s.trim()).filter(Boolean);
        if (ids.length > 0) {
          setFileIds(ids);
          /* After successful upload, mark documents as uploaded (clear File object, set fileId).
             This prevents re-upload when the user goes Back and clicks Next again. */
          if (stepIndex === 0) {
            setDocuments(prev => prev.map((doc, i) => ({
              ...doc,
              file: null,              // File already uploaded — clear to prevent re-upload
              fileId: ids[i] || doc.fileId,
            })));
            /* Also clear File objects from additional slot files so they are not re-uploaded
               on a subsequent Back → Next navigation. */
            setAdditionalSlotFiles(prev => prev.map(sf => ({ ...sf, file: null })));
          }
        }

        /* Parse file URLs — don't accumulate names on each save */
        if (Array.isArray(res.data.files) && res.data.files.length > 0) {
          setFileURL(res.data.files.join(','));
          const primaryNames = isDriveSelected ? selectedDriveData.fileNames : fileNames;
          /* Only include names for slots whose File is being freshly uploaded this call.
             Slots whose file was already cleared (null) have their names in fileNames already
             from the previous call, so including them again would double-count. */
          const extraNames = [
            ...additionalSlotFiles.filter(sf => sf.file).map(sf => sf.fileName).filter(Boolean),
            ...additionalSlotDrives.flatMap(sd => sd.driveData?.fileNames ?? []),
          ];
          const allNames = [...primaryNames, ...extraNames];
          setFileNames(allNames);
          setFileNamesForDataTable(allNames);
        }

        setActiveIndex(stepIndex + 1);
      } catch (err) {
        handle401(err);
        showToast('error', 'Failed to save draft');
      } finally {
        setLoading(false);
        setProgress(0);
        setProcessing(false);
      }
    },
    [
      transactionId, navigate, documents, fileIds, fileNames, isDriveSelected, selectedDriveData,
      selectedQuote, selectedPolicy, selectedQuotesList, selectedPoliciesList,
      quoteCoverageDetails, quoteDriverLists, quoteVehicleLists,
      additionalSlotFiles, additionalSlotDrives,
      coverageDetails, driverList, vehicleList, clientId, clientName,
      clientEmail, proposalTitle, selectedTemplate, videoUrl, fileURL, teamMember,
      proposalTeamMembers, extraDocumentList, dynamicLinks, customBlocks, handle401, showToast,
    ]
  );

  /* ═══════════════════════════════════════════════════
     K. LOAD DRAFT (on mount if URL has transactionId)
  ═══════════════════════════════════════════════════ */
  const loadDraft = useCallback(
    async (txId: string, signal?: AbortSignal) => {
      try {
        const res = await axios.get(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/getdraftdata`, {
          params: { transactionId: txId },
          signal,
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
        });
        const d = res.data;

        let hasUploadedFiles = false;
        let hasDriveFiles = false;
        let hasQuoteSelection = false;
        let restoredQuotesFromDraft: any[] = [];
        let restoredPoliciesFromDraft: any[] = [];

        /* File IDs */
        let ids: string[] = [];
        if (Array.isArray(d.fileId)) ids = d.fileId;
        else if (typeof d.fileId === 'string')
          ids = d.fileId.split(',').map((s: string) => s.trim()).filter(Boolean);
        if (ids.length > 0) setFileIds(ids);

        /* Files / names */
        const urls = typeof d.fileURL === 'string'
          ? d.fileURL.split(',').map((s: string) => s.trim()).filter(Boolean)
          : (Array.isArray(d.fileURL) ? d.fileURL : []);
        const namesFromApi = typeof d.fileName === 'string'
          ? d.fileName.split(',').map((s: string) => s.trim()).filter(Boolean)
          : (Array.isArray(d.fileName) ? d.fileName : []);
        const namesFromUrls = urls.map((u: string) => {
          try {
            const path = new URL(u).pathname;
            const encodedName = path.substring(path.lastIndexOf('/') + 1);
            return decodeURIComponent(encodedName || '').trim();
          } catch {
            return '';
          }
        });
        const hasReliableUrlNames = namesFromUrls.length > 0 && namesFromUrls.every(Boolean);
        const names = hasReliableUrlNames
          ? namesFromUrls
          : (namesFromApi.length > 0 ? namesFromApi : namesFromUrls.filter(Boolean));

        if (names.length > 0 || urls.length > 0) {
          const rowCount = Math.max(names.length, urls.length, ids.length);
          const restoredDocs = Array.from({ length: rowCount }, (_, i) => ({
            file: null,
            fileName: names[i] || '',
            fileURL: urls[i] || '',
            fileId: ids[i] || '',
          })).filter((doc) => !!(doc.fileName || doc.fileURL || doc.fileId));

          const restoredNames = restoredDocs.map((doc) => doc.fileName).filter(Boolean);
          setFileNames(restoredNames);
          setFileNamesForDataTable(restoredNames);
          setFileURL(typeof d.fileURL === 'string' ? d.fileURL : urls.join(','));
          const primaryDoc = restoredDocs[0] || { file: null, fileName: '', fileURL: '', fileId: '' };
          setDocuments([primaryDoc]);
          setRestoredAdditionalDocs(
            restoredDocs.slice(1).map((doc) => ({
              fileName: doc.fileName,
              fileURL: doc.fileURL,
              fileId: doc.fileId,
            }))
          );
          hasUploadedFiles = restoredDocs.length > 0;
        } else {
          setRestoredAdditionalDocs([]);
        }

        setClientName(d.clientName || '');
        setClientId(d.clientId || '');
        setClientEmail(d.clientEmail || '');
        setProposalTitle(d.proposalTitle || '');
        setVideoUrl(d.videoUrl || '');
        setTeamMember(d.selectedTeamMember || '');

        if (d.templateId) {
          setSelectedTemplate(String(d.templateId));
        }

        /* Restore proposal team members — API field is selectedProposalMembers */
        const rawMembers = d.selectedProposalMembers ?? d.proposalTeamMembers;
        if (rawMembers) {
          try {
            setProposalTeamMembers(
              typeof rawMembers === 'string' ? JSON.parse(rawMembers) : rawMembers
            );
          } catch {
            setProposalTeamMembers([]);
          }
        }

        if (d.extraDocDetails) {
          try {
            setExtraDocumentList(JSON.parse(d.extraDocDetails));
          } catch {
            setExtraDocumentList([]);
          }
        }

        if (d.dynamicLinks) {
          try {
            const links = typeof d.dynamicLinks === 'string' ? JSON.parse(d.dynamicLinks) : d.dynamicLinks;
            setDynamicLinks(Array.isArray(links) ? links : []);
          } catch {
            setDynamicLinks([]);
          }
        }

        const parseDraftDetails = (raw: any): any[] => {
          if (!raw) return [];
          if (Array.isArray(raw)) return raw.filter(Boolean);
          if (typeof raw === 'object') return [raw];
          if (typeof raw !== 'string') return [];

          const text = raw.trim();
          if (!text) return [];

          try {
            const parsed = JSON.parse(text);
            return Array.isArray(parsed) ? parsed.filter(Boolean) : [parsed];
          } catch {
            // Fallback for backend format: {json}|||{json}
          }

          return text
            .split('|||')
            .map(part => part.trim())
            .filter(Boolean)
            .map(part => {
              try {
                return JSON.parse(part);
              } catch {
                return null;
              }
            })
            .filter(Boolean) as any[];
        };

        /* Restore quote / coverage / driver / vehicle from quoteDetails JSON */
        if (d.quoteDetails) {
          try {
            const quoteArray = parseDraftDetails(d.quoteDetails);
            const restoredQuotes = quoteArray
              .filter((q: any) => q && (q['DatabaseId'] || q['databaseId']))
              .map((q: any) => ({
                databaseId:          q['DatabaseId'] || q['databaseId'],
                policyNumber:        q['PolicyNumber'] || q['policyNumber'] || q['Policy Number'] || '',
                totalPremium:        q['TotalPremium'] ?? q['totalPremium'] ?? q['Total Premium'] ?? 0,
                carrierName:         q['CarrierName'] || q['carrierName'] || q['Carrier Name'] || '',
                policyTermInMonths:  q['Term'] ?? q['policyTermInMonths'] ?? null,
                effectiveDate:       q['EffectiveDate'] || q['effectiveDate'] || q['Effective Date'] || '',
                expirationDate:      q['ExpirationDate'] || q['expirationDate'] || q['Expiration Date'] || '',
                linesOfBusiness:     q['DocumentType'] ? [q['DocumentType']] : (q['linesOfBusiness'] || []),
                isQuote:             true,
              }));
            if (restoredQuotes.length > 0) {
              restoredQuotesFromDraft = restoredQuotes;
              setSelectedQuoteId(restoredQuotes[0].databaseId);
              setSelectedQuote(restoredQuotes[0]);
              hasQuoteSelection = true;

              const covLists = quoteArray.map((q: any) =>
                Array.isArray(q['CoverageDetails'])
                  ? q['CoverageDetails']
                  : (Array.isArray(q['Coverage Details']) ? q['Coverage Details'] : [])
              );
              const drvLists = quoteArray.map((q: any) =>
                Array.isArray(q['DriverList'])
                  ? q['DriverList']
                  : (Array.isArray(q['Driver List']) ? q['Driver List'] : [])
              );
              const vehLists = quoteArray.map((q: any) =>
                Array.isArray(q['VehicleList'])
                  ? q['VehicleList']
                  : (Array.isArray(q['Vehicle List']) ? q['Vehicle List'] : [])
              );
              setCoverageDetails(covLists[0] || []);
              setDriverList(drvLists[0] || []);
              setVehicleList(vehLists[0] || []);
              setQuoteCoverageDetails(covLists);
              setQuoteDriverLists(drvLists);
              setQuoteVehicleLists(vehLists);
            }
          } catch {
            // leave quote state empty
          }
        }

        /* Restore policy from policyDetails JSON */
        if (d.policyDetails) {
          try {
            const policyArray = parseDraftDetails(d.policyDetails);
            const restoredPolicies = policyArray
              .filter((p: any) => p && (p['DatabaseId'] || p['databaseId']))
              .map((p: any) => ({
                databaseId:         p['DatabaseId'] || p['databaseId'],
                policyNumber:       p['PolicyNumber'] || p['policyNumber'] || p['Policy Number'] || '',
                totalPremium:       p['TotalPremium'] ?? p['totalPremium'] ?? p['Total Premium'] ?? 0,
                carrierName:        p['CarrierName'] || p['carrierName'] || p['Carrier Name'] || '',
                policyTermInMonths: p['Term'] ?? p['policyTermInMonths'] ?? null,
                effectiveDate:      p['EffectiveDate'] || p['effectiveDate'] || p['Effective Date'] || '',
                expirationDate:     p['ExpirationDate'] || p['expirationDate'] || p['Expiration Date'] || '',
                lineOfBusiness:     p['DocumentType'] || p['lineOfBusiness'] || p['linesOfBusiness']?.[0] || '',
                linesOfBusiness:    p['linesOfBusiness'] || (p['lineOfBusiness'] ? [p['lineOfBusiness']] : []),
                isQuote:            false,
              }));
            if (restoredPolicies.length > 0) {
              restoredPoliciesFromDraft = restoredPolicies;
              setSelectedPolicyId(restoredPolicies[0].databaseId);
              setSelectedPolicy(restoredPolicies[0]);
              setSelectedPoliciesList(restoredPolicies);
              hasQuoteSelection = true;
            }
          } catch {
            // leave policy state empty
          }
        }

        {
          const maxLen = Math.max(restoredQuotesFromDraft.length, restoredPoliciesFromDraft.length);
          if (maxLen > 0) {
            const mergedForSlots = Array.from({ length: maxLen }, (_, i) =>
              restoredQuotesFromDraft[i] || restoredPoliciesFromDraft[i] || null
            );
            setSelectedQuotesList(mergedForSlots);
            if (restoredPoliciesFromDraft.length === 0) {
              setSelectedPoliciesList(Array.from({ length: maxLen }, (_, i) => null));
            }
          }
        }

        if (d.clientId) getNowCertsPoliciesList(d.clientId);

        /* Restore drive (Client Profile) file selection */
        if (d.folderId) {
          const driveFileIds   = typeof d.driveFileIds === 'string'
            ? d.driveFileIds.split(',').filter(Boolean)
            : (Array.isArray(d.driveFileIds) ? d.driveFileIds : []);
          const driveFolderIds = typeof d.driveFolderIds === 'string'
            ? d.driveFolderIds.split(',').filter(Boolean)
            : (Array.isArray(d.driveFolderIds) ? d.driveFolderIds : []);
          const driveFileNames = namesFromUrls.length > 0
            ? namesFromUrls
            : namesFromApi;
          if (driveFileIds.length > 0) {
            hasDriveFiles = true;
            setSelectedDriveData({
              folderId:  d.folderId,
              fileIds:   driveFileIds,
              fileNames: driveFileNames,
              folderIds: driveFolderIds,
            });
            setFileNamesForDataTable(driveFileNames);
          }
        }

        if (hasDriveFiles) {
          setIsDriveSelected(true);
          setIsDocumentSelected(false);
          setIsQuoteSelected(false);
        } else if (hasUploadedFiles) {
          setIsDocumentSelected(true);
          setIsDriveSelected(false);
          setIsQuoteSelected(false);
        } else if (hasQuoteSelection) {
          setIsQuoteSelected(true);
          setIsDocumentSelected(false);
          setIsDriveSelected(false);
        }

        /* Always start at Step 1 when loading from URL/history */
        setActiveIndex(0);
      } catch (err) {
        if (axios.isCancel(err)) return; // aborted — navigation happened before response
        handle401(err);
      }
    },
    [handle401, getNowCertsPoliciesList, setSelectedTemplate, setSelectedQuoteId, setSelectedQuote, setIsQuoteSelected, setIsDocumentSelected, setIsDriveSelected, setCoverageDetails, setDriverList, setVehicleList, setSelectedPolicyId, setSelectedPolicy, setSelectedQuotesList, setSelectedPoliciesList, setQuoteCoverageDetails, setQuoteDriverLists, setQuoteVehicleLists]
  );

  /* ═══════════════════════════════════════════════════
     L. UPLOAD EXTRA DOCUMENT — IMMEDIATE in Step 2
  ═══════════════════════════════════════════════════ */
  const uploadExtraDocument = useCallback(
    async (file: File) => {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) {
        showToast('warning', 'Only PDF files are allowed');
        return;
      }
      if (!transactionId) {
        showToast('warning', 'Please complete Step 1 first');
        return;
      }
      setUploadDocLoading(true);

      const data = new FormData();
      data.append('file', file);
      data.append('transactionId', transactionId);

      try {
        const res = await axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/uploadextradoc`, data, {
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
        });
        setExtraDocumentList(prev => [
          ...prev,
          { fileURL: res.data.fileURL || '', fileName: res.data.fileName || file.name },
        ]);
      } catch (err) {
        handle401(err);
        showToast('error', 'Failed to upload document');
      } finally {
        setUploadDocLoading(false);
      }
    },
    [transactionId, handle401, showToast]
  );

  /* ═══════════════════════════════════════════════════
     ON MOUNT
  ═══════════════════════════════════════════════════ */
  useEffect(() => {
    getAllSettings();
    getTeamMemberList();
  }, [getAllSettings, getTeamMemberList]);

  useEffect(() => {
    if (!urlTxId) {
      // URL has no transaction ID — clear state so the next save triggers a fresh navigate
      setTransactionId('');
      return;
    }
    if (skipLoadDraftRef.current) { skipLoadDraftRef.current = false; return; }
    const controller = new AbortController();
    setTransactionId(urlTxId);
    loadDraft(urlTxId, controller.signal);
    return () => controller.abort(); // cancel if URL changes or component unmounts
  }, [urlTxId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ═══════════════════════════════════════════════════
     VALIDATION — STEP 1
  ═══════════════════════════════════════════════════ */
  const validateStep1 = useCallback((): boolean => {
    const errs: Record<string, boolean> = {};

    if (!clientName.trim()) {
      errs.clientName = true;
      showToast('warning', 'Please select a client');
    }
    if (!proposalTitle.trim()) {
      errs.proposalTitle = true;
      showToast('warning', 'Please enter a proposal title');
    }

    if (isDocumentSelected) {
      const hasDoc = documents.some(d => d.fileName.trim());
      if (!hasDoc) {
        errs.documentRequired = true;
        showToast('warning', 'Please upload at least one document');
      }
    } else if (isDriveSelected && selectedDriveData.fileIds.length === 0) {
      errs.driveRequired = true;
      showToast('warning', 'Please select a file from client profile');
    } else if (isQuoteSelected && !selectedQuoteId) {
      errs.quoteRequired = true;
      showToast('warning', 'Please select a quote');
    }

    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  }, [
    clientName, proposalTitle, isDocumentSelected, isDriveSelected, isQuoteSelected,
    documents, selectedDriveData.fileIds.length, selectedQuoteId, selectedPolicyId, showToast,
  ]);

  /* ═══════════════════════════════════════════════════
     NEXT / BACK HANDLERS
  ═══════════════════════════════════════════════════ */
  const handleNext = useCallback(async () => {
    if (isSubmittingRef.current || loading) return;
    if (activeIndex === 0) {
      if (!validateStep1()) return;
    }
    isSubmittingRef.current = true;
    try {
      await createDraft(activeIndex);
    } finally {
      isSubmittingRef.current = false;
    }
  }, [activeIndex, loading, validateStep1, createDraft]);

  const handleBack = useCallback(() => {
    if (activeIndex > 0) setActiveIndex(i => i - 1);
    else navigate(ROUTES.AMPS);
  }, [activeIndex, navigate]);

  const handleNewProposal = useCallback(() => {
    /* Reset all form state and return to step 1 */
    setActiveIndex(0);
    setTransactionId('');
    setProposalTitle('');
    setClientName(''); setClientId(''); setClientEmail(''); setSearch(''); setSearchClientList([]);
    setIsDocumentSelected(true); setIsDriveSelected(false); setIsQuoteSelected(false);
    setDocuments([{ file: null, fileName: '', fileURL: '', fileId: '' }]);
    setFileIds([]); setFileNames([]); setFileURL(''); setFileNamesForDataTable([]);
    setSelectedDriveData({ folderId: '', fileIds: [], fileNames: [], folderIds: [] });
    setNowCertQuoteData([]); setNowCertPolicyData([]);
    setSelectedQuoteId(''); setSelectedQuote(null);
    setSelectedPolicyId(''); setSelectedPolicy(null);
    setCoverageDetails([]); setDriverList([]); setVehicleList([]);
    setSelectedQuotesList([]); setSelectedPoliciesList([]);
    setQuoteCoverageDetails([]); setQuoteDriverLists([]); setQuoteVehicleLists([]);
    setAdditionalSlotFiles([]); setAdditionalSlotDrives([]);
    setRestoredAdditionalDocs([]);
    setTeamMember(''); setProposalTeamMembers([]);
    setSelectedTemplate('1'); setSelectedCustomTemplate(0);
    setSelectedCustomTemplateName(''); setSelectedCustomTemplateImages([]);
    setVideoUrl(''); setExtraDocumentList([]); setDynamicLinks([]);
    setValidationErrors({});
    setIsProposalGenerated(false);
    navigate(ROUTES.QUOTE_PROPOSAL, { replace: true });
  }, [navigate]);

  const handleStepClick = useCallback(
    (i: number) => {
      if (i < activeIndex && !isProposalGenerated && !isGenerating) setActiveIndex(i);
    },
    [activeIndex, isProposalGenerated, isGenerating]
  );

  /* ═══════════════════════════════════════════════════
     QUOTE SELECTED HANDLER
  ═══════════════════════════════════════════════════ */
  const handleQuoteSelected = useCallback(
    (quote: any, slotIndex?: number) => {
      if (quote.isQuote === true) {
        if (slotIndex !== undefined && slotIndex > 0) {
          /* Toggle: deselect if same quote already in this slot */
          const isAlreadySelected = selectedQuotesList[slotIndex]?.databaseId === quote.databaseId;
          setSelectedQuotesList(prev => {
            const u = [...prev];
            while (u.length <= slotIndex) u.push(null);
            u[slotIndex] = isAlreadySelected ? null : quote;
            return u;
          });
          /* Clear the policy for this slot whenever the quote changes or is removed */
          setSelectedPoliciesList(prev => {
            const u = [...prev];
            while (u.length <= slotIndex) u.push(null);
            u[slotIndex] = null;
            return u;
          });
          /* Fetch coverage outside the state updater so it's a clean async side-effect */
          if (!isAlreadySelected) {
            getPolicyCoverageDetailList(quote.databaseId).then((details: any) => {
              setQuoteCoverageDetails(p => { const a = [...p]; a[slotIndex] = details || []; return a; });
            }).catch(() => {});
          }
        } else {
          /* Primary quote slot */
          const isAlreadySelected = selectedQuoteId === quote.databaseId;
          setSelectedQuoteId(isAlreadySelected ? '' : quote.databaseId);
          setSelectedQuote(isAlreadySelected ? null : quote);
          if (!isAlreadySelected) {
            getPolicyCoverageDetailList(quote.databaseId);
            getDriverListFn(clientId);
            getVehicleListFn(clientId);
            /* Sync into list at index 0 */
            setSelectedQuotesList(prev => { const u = [...prev]; u[0] = quote; return u; });
          } else {
            setCoverageDetails([]);
            setDriverList([]);
            setVehicleList([]);
            setSelectedQuotesList(prev => prev.filter((_, i) => i !== 0));
            /* Clear primary slot policy when quote is deselected */
            setSelectedPolicyId('');
            setSelectedPolicy(null);
            setSelectedPoliciesList(prev => { const u = [...prev]; if (u.length > 0) u[0] = null; return u; });
          }
        }
      } else {
        /* Policy selection — route to correct slot */
        if (slotIndex !== undefined && slotIndex > 0) {
          setSelectedPoliciesList(prev => {
            const u = [...prev];
            while (u.length <= slotIndex) u.push(null);
            u[slotIndex] = u[slotIndex]?.databaseId === quote.databaseId ? null : quote;
            return u;
          });
        } else {
          const isAlreadySelected = selectedPolicyId === quote.databaseId;
          setSelectedPolicyId(isAlreadySelected ? '' : quote.databaseId);
          setSelectedPolicy(isAlreadySelected ? null : quote);
        }
      }
    },
    [clientId, selectedQuoteId, selectedPolicyId, selectedQuotesList, getPolicyCoverageDetailList, getDriverListFn, getVehicleListFn]
  );

  const handleAddQuote = useCallback(() => {
    /* Additional panels are 1-based (slot 1, 2, ...); reserve index 0 for primary slot. */
    setSelectedQuotesList(prev => {
      const u = [...prev];
      const nextSlotIndex = Math.max(1, u.length);
      while (u.length <= nextSlotIndex) u.push(null);
      return u;
    });
    setQuoteCoverageDetails(prev => {
      const u = [...prev];
      const nextSlotIndex = Math.max(1, u.length);
      while (u.length <= nextSlotIndex) u.push([]);
      return u;
    });
    setQuoteDriverLists(prev => {
      const u = [...prev];
      const nextSlotIndex = Math.max(1, u.length);
      while (u.length <= nextSlotIndex) u.push([]);
      return u;
    });
    setQuoteVehicleLists(prev => {
      const u = [...prev];
      const nextSlotIndex = Math.max(1, u.length);
      while (u.length <= nextSlotIndex) u.push([]);
      return u;
    });
  }, []);

  const handleRemoveLastQuote = useCallback((slotIndex?: number) => {
    if (slotIndex !== undefined) {
      setSelectedQuotesList(prev => prev.filter((_, i) => i !== slotIndex));
      setSelectedPoliciesList(prev => prev.filter((_, i) => i !== slotIndex));
      setQuoteCoverageDetails(prev => prev.filter((_, i) => i !== slotIndex));
      setQuoteDriverLists(prev => prev.filter((_, i) => i !== slotIndex));
      setQuoteVehicleLists(prev => prev.filter((_, i) => i !== slotIndex));
      setAdditionalSlotFiles(prev => prev.filter(s => s.slotIndex !== slotIndex));
      setAdditionalSlotDrives(prev => prev.filter(s => s.slotIndex !== slotIndex));
      setRestoredAdditionalDocs(prev => prev.filter((_, i) => i !== slotIndex - 1));

      /* Keep uploaded metadata arrays aligned by slot position (index 0 is primary doc). */
      setFileIds(prev => prev.filter((_, i) => i !== slotIndex));
      setFileNames(prev => prev.filter((_, i) => i !== slotIndex));
      setFileNamesForDataTable(prev => prev.filter((_, i) => i !== slotIndex));
      setFileURL(prev => prev
        .split(',')
        .map(u => u.trim())
        .filter(Boolean)
        .filter((_, i) => i !== slotIndex)
        .join(','));
    } else {
      setSelectedQuotesList(prev => prev.slice(0, -1));
      setSelectedPoliciesList(prev => prev.slice(0, -1));
      setQuoteCoverageDetails(prev => prev.slice(0, -1));
      setQuoteDriverLists(prev => prev.slice(0, -1));
      setQuoteVehicleLists(prev => prev.slice(0, -1));
    }
  }, []);

  const handleSlotClear = useCallback((slotIndex: number) => {
    setAdditionalSlotFiles(prev => prev.filter(s => s.slotIndex !== slotIndex));
    setAdditionalSlotDrives(prev => prev.filter(s => s.slotIndex !== slotIndex));
    setSelectedQuotesList(prev => {
      const u = [...prev];
      if (u.length > slotIndex) u[slotIndex] = null;
      return u;
    });
    setSelectedPoliciesList(prev => {
      const u = [...prev];
      if (u.length > slotIndex) u[slotIndex] = null;
      return u;
    });
    setQuoteCoverageDetails(prev => {
      const u = [...prev];
      if (u.length > slotIndex) u[slotIndex] = [];
      return u;
    });
    setQuoteDriverLists(prev => {
      const u = [...prev];
      if (u.length > slotIndex) u[slotIndex] = [];
      return u;
    });
    setQuoteVehicleLists(prev => {
      const u = [...prev];
      if (u.length > slotIndex) u[slotIndex] = [];
      return u;
    });
  }, []);

  const handleAdditionalSlotFile = useCallback((slotIndex: number, file: File | null, fileName: string) => {
    if (!file) {
      setAdditionalSlotFiles(prev => prev.filter(s => s.slotIndex !== slotIndex));
    } else {
      setAdditionalSlotFiles(prev => {
        const filtered = prev.filter(s => s.slotIndex !== slotIndex);
        return [...filtered, { slotIndex, file, fileName }];
      });
    }
  }, []);

  const handleAdditionalSlotDrive = useCallback((slotIndex: number, driveData: import('./Step1_ClientQuote').DriveData | null) => {
    if (!driveData) {
      setAdditionalSlotDrives(prev => prev.filter(s => s.slotIndex !== slotIndex));
    } else {
      setAdditionalSlotDrives(prev => {
        const filtered = prev.filter(s => s.slotIndex !== slotIndex);
        return [...filtered, { slotIndex, driveData }];
      });
    }
  }, []);

  /* ═══════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════ */
  return (
    <div className="qp-stepper-root">

      {/* ── Stepper header ── */}
      <div className="qp-stepper">
        {STEPS.map((step, i) => (
          <React.Fragment key={step.num}>
            <div
              className={`qp-stepper__step${i === activeIndex ? ' qp-stepper__step--active' : ''}${i < activeIndex ? ' qp-stepper__step--done' : ''}`}
              onClick={() => handleStepClick(i)}
              style={i < activeIndex && !isProposalGenerated && !isGenerating ? { cursor: 'pointer' } : undefined}
              role="button"
              tabIndex={i < activeIndex && !isProposalGenerated && !isGenerating ? 0 : -1}
              onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && i < activeIndex) handleStepClick(i); }}
            >
              <span className="qp-stepper__num">
                {i < activeIndex ? <FiCheck size={13} /> : step.num}
              </span>
              <span className="qp-stepper__label">{step.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`qp-stepper__line${i < activeIndex ? ' qp-stepper__line--done' : i === activeIndex ? ' qp-stepper__line--active' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── Top progress bar (while saving draft) ── */}
      {loading && progress > 0 && (
        <div className="qp-progress-bar">
          <div className="qp-progress-bar__fill" style={{ width: `${progress}%` }} />
          {processing && <span className="qp-progress-bar__text">Processing…</span>}
        </div>
      )}

      {/* ── Step content ── */}
      <div className="qp-step-body">
        {activeIndex === 0 && (
          <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.22 }}>
            <Step1_ClientQuote
              search={search}
              clientName={clientName}
              clientId={clientId}
              clientEmail={clientEmail}
              proposalTitle={proposalTitle}
              searchClientList={searchClientList}
              isDocumentSelected={isDocumentSelected}
              isDriveSelected={isDriveSelected}
              isQuoteSelected={isQuoteSelected}
              documents={documents}
              fileURL={fileURL}
              fileNamesForDataTable={fileNamesForDataTable}
              selectedDriveData={selectedDriveData}
              nowCertQuoteData={nowCertQuoteData}
              nowCertPolicyData={nowCertPolicyData}
              selectedQuoteId={selectedQuoteId}
              selectedQuote={selectedQuote}
              selectedPolicyId={selectedPolicyId}
              selectedPolicy={selectedPolicy}
              nowcertsPolicyLoading={nowcertsPolicyLoading}
              clientSearchLoading={clientSearchLoading}
              uploadingDocIndex={uploadingDocIndex}
              progress={progress}
              validationErrors={validationErrors}
              onSearchChange={handleSearchChange}
              onClientSelect={handleClientSelect}
              onDocSourceChange={(src) => {
                if (src !== 'upload') {
                  setDocuments([{ file: null, fileName: '', fileURL: '', fileId: '' }]);
                  setFileIds([]);
                  setFileNames([]);
                  setFileURL('');
                }
                if (src !== 'clientProfile') {
                  setSelectedDriveData({ folderId: '', fileIds: [], fileNames: [], folderIds: [] });
                }
                if (src !== 'ams') {
                  setSelectedQuoteId('');
                  setSelectedQuote(null);
                  setCoverageDetails([]);
                  setDriverList([]);
                  setVehicleList([]);
                }
                setSelectedPolicyId('');
                setSelectedPolicy(null);
                setSelectedPoliciesList(prev => { const u = [...prev]; if (u.length > 0) u[0] = null; return u; });
                setFileNamesForDataTable([]);
                setIsDocumentSelected(src === 'upload');
                setIsDriveSelected(src === 'clientProfile');
                setIsQuoteSelected(src === 'ams');
              }}
              onDocumentFileDrop={(file, idx) => {
                setDocuments(prev => {
                  const u = [...prev];
                  if (!u[idx]) u[idx] = { file: null, fileName: '', fileURL: '', fileId: '' };
                  u[idx] = { file, fileName: file.name, fileURL: '', fileId: '' };
                  return u;
                });
                setFileNames(prev => {
                  const u = [...prev];
                  u[idx] = file.name;
                  return u.filter(Boolean);
                });
                setFileNamesForDataTable(prev => {
                  const u = [...prev];
                  u[idx] = file.name;
                  return u.filter(Boolean);
                });
              }}
              onDocumentRemove={(idx) => {
                setDocuments(prev => {
                  const next = prev.filter((_, i) => i !== idx);
                  return next.length > 0 ? next : [{ file: null, fileName: '', fileURL: '', fileId: '' }];
                });
                setFileIds(prev => prev.filter((_, i) => i !== idx));
                setFileNames(prev => prev.filter((_, i) => i !== idx));
                setFileNamesForDataTable(prev => prev.filter((_, i) => i !== idx));
              }}
              onAddDocSlot={() => setDocuments(prev => [...prev, { file: null, fileName: '', fileURL: '', fileId: '' }])}
              onDriveFileSelect={(d) => {
                if (d.fileIds.length <= 1) {
                  setSelectedDriveData(d);
                  setFileNamesForDataTable(d.fileNames);
                  setAdditionalSlotDrives([]);
                } else {
                  const primary = { folderId: d.folderId, fileIds: [d.fileIds[0]], fileNames: [d.fileNames[0]], folderIds: [d.folderIds[0]] };
                  setSelectedDriveData(primary);
                  setFileNamesForDataTable([d.fileNames[0]]);
                  setAdditionalSlotDrives(
                    d.fileIds.slice(1).map((fileId, i) => ({
                      slotIndex: i + 1,
                      driveData: { folderId: d.folderId, fileIds: [fileId], fileNames: [d.fileNames[i + 1] || ''], folderIds: [d.folderIds[i + 1] || ''] },
                    }))
                  );
                }
              }}
              onQuoteSelected={handleQuoteSelected}
              selectedQuotesList={selectedQuotesList}
              selectedPoliciesList={selectedPoliciesList}
              restoredAdditionalDocs={restoredAdditionalDocs}
              additionalSlotFiles={additionalSlotFiles}
              additionalSlotDrives={additionalSlotDrives}
              onAddQuote={handleAddQuote}
              onRemoveQuote={handleRemoveLastQuote}
              onAdditionalSlotFile={handleAdditionalSlotFile}
              onAdditionalSlotDrive={handleAdditionalSlotDrive}
              onSlotClear={handleSlotClear}
              onInvalidPdfFile={() => showToast('warning', 'Only PDF files are allowed')}
              onViewDocumentClicked={onViewDocumentClicked}
              onProposalTitleChange={(v) => {
                setProposalTitle(v);
                setValidationErrors(e => ({ ...e, proposalTitle: false }));
              }}
            />
          </motion.div>
        )}

        {activeIndex === 1 && (
          <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.22 }}>
            <Step2_SignatureDocsVideo
              fileNamesForDataTable={fileNamesForDataTable}
              fileURL={fileURL}
              teamMemberList={teamMemberList}
              teamMember={teamMember}
              proposalTeamMembers={proposalTeamMembers}
              selectedTemplate={selectedTemplate}
              selectedCustomTemplate={selectedCustomTemplate}
              selectedCustomTemplateName={selectedCustomTemplateName}
              selectedCustomTemplateImages={selectedCustomTemplateImages}
              videoUrl={videoUrl}
              extraDocumentList={extraDocumentList}
              uploadDocLoading={uploadDocLoading}
              customBlocks={customBlocks}
              transactionId={transactionId}
              customTemplateList={customTemplateList}
              dynamicLinks={dynamicLinks}
              onTemplateChange={(id, name, images, customId) => {
                setSelectedTemplate(id);
                setSelectedCustomTemplateName(name);
                setSelectedCustomTemplateImages(images);
                setSelectedCustomTemplate(customId ?? 0);
                fetchCustomBlocksForTemplate(id);
              }}
              onSignatureChange={setTeamMember}
              onTeamMembersChange={setProposalTeamMembers}
              onVideoUrlChange={setVideoUrl}
              onExtraDocFileDrop={uploadExtraDocument}
              onExtraDocRemove={(i) => setExtraDocumentList(prev => prev.filter((_, idx) => idx !== i))}
              onCustomBlocksChange={setCustomBlocks}
              onDynamicLinksChange={setDynamicLinks}
              onInvalidPdfFile={() => showToast('warning', 'Only PDF files are allowed')}
              onViewDocumentClicked={onViewDocumentClicked}
            />
          </motion.div>
        )}

        {activeIndex === 2 && (
          <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.22 }}>
            <Step3_PreviewSend
              clientName={clientName}
              clientId={clientId}
              clientEmail={clientEmail}
              proposalTitle={proposalTitle}
              transactionId={transactionId}
              fileIds={fileIds}
              fileNames={fileNamesForDataTable}
              fileURL={fileURL}
              isDriveSelected={isDriveSelected}
              selectedDriveData={selectedDriveData}
              additionalSlotDrives={additionalSlotDrives}
              selectedTemplate={selectedTemplate}
              selectedCustomTemplate={selectedCustomTemplate}
              selectedCustomTemplateImages={selectedCustomTemplateImages}
              teamMember={teamMember}
              proposalTeamMembers={proposalTeamMembers}
              videoUrl={videoUrl}
              extraDocumentList={extraDocumentList}
              customBlocks={customBlocks}
              dynamicLinks={dynamicLinks}
              companyData={companyData}
              selectedQuote={selectedQuote}
              selectedPolicy={selectedPolicy}
              selectedQuotesList={selectedQuotesList}
              selectedPoliciesList={selectedPoliciesList}
              quoteCoverageDetails={quoteCoverageDetails}
              quoteDriverLists={quoteDriverLists}
              quoteVehicleLists={quoteVehicleLists}
              coverageDetails={coverageDetails}
              driverList={driverList}
              vehicleList={vehicleList}
              onBack={() => setActiveIndex(1)}
              onGenerating={() => setIsGenerating(true)}
              onGenerated={() => { setIsProposalGenerated(true); setIsGenerating(false); }}
              onNewProposal={handleNewProposal}
            />
          </motion.div>
        )}
      </div>

      {/* ── Bottom actions ── */}
      {activeIndex < 2 && (
        <div className="qp-actions">
          <button className="qp-actions__cancel glass-btn" onClick={handleBack} disabled={loading}>
            {activeIndex === 0 ? 'Cancel' : '← Back'}
          </button>
          <button
            className="qp-actions__next glass-btn glass-btn-primary"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="qp-spinner qp-spinner--small" />
                {processing ? 'Processing…' : 'Saving…'}
              </>
            ) : (
              'Next Step →'
            )}
          </button>
        </div>
      )}
      {activeIndex === 2 && !isProposalGenerated && !isGenerating && (
        <div className="qp-actions">
          <button className="qp-actions__cancel glass-btn" onClick={() => setActiveIndex(1)}>
            ← Back
          </button>
        </div>
      )}

      {/* ── Document viewer ── */}
      {viewDocOpen && viewDocUrl && (
        <div className="qp-dialog-overlay" onClick={() => setViewDocOpen(false)}>
          <motion.div
            className="qp-dialog qp-dialog--large"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="qp-dialog__header">
              <h3>Document Preview</h3>
              <button className="qp-dialog__close" onClick={() => setViewDocOpen(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="qp-dialog__body" style={{ padding: 0 }}>
              <iframe src={viewDocUrl} title="Preview" className="qp-dialog__iframe" />
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default QuoteStepper;

